import { useCallback, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import LogTool from '../../../logger/logTools'
import { fetchChildrenPage } from '../api/bomApi'
import { Part, type Article } from '../../Item/utils/types'
import { APIArticleFromJSON, type APIArticle } from '../../../generated-types/models/APIArticle'
import { type PaginatedResponse } from '../../../api/client'
import { getPKfromSelf } from '../../../utils/functions'

const log = new LogTool({ context: 'usePartChildren', enable: true, logLevel: 'warn' })

export type UsePartChildrenOptions = {
  requestSelf?: string | null
  accessSecret?: string | null
  authHeaders?: Record<string, string>
  parameter?: string[]
  maxDepth?: number
  onNode?: (parent: APIArticle, children: APIArticle[]) => void
  onFinal?: () => void
  onError?: (error: any) => void
}

export type UsePartChildrenResult = {
  loadChildren: (part: Part) => Promise<void>
  isLoading: boolean
  isError: boolean
  error: Error | null
}

/** Cache durations for children pagination queries (conservative) */
const CHILDREN_STALE_TIME_MS = 30_000 // 30 seconds
const CHILDREN_GC_TIME_MS = 5 * 60 * 1000 // 5 minutes

export function usePartChildren(options: UsePartChildrenOptions): UsePartChildrenResult {
  const {
    requestSelf,
    accessSecret,
    authHeaders,
    // Default: no requests expand - it's expensive and not needed for mappings
    parameter = ['expand=files,properties'],
    maxDepth = Infinity,
    onNode = () => null,
    onFinal = () => null,
    onError,
  } = options

  const [status, setStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle')
  const [error, setError] = useState<Error | null>(null)
  const queryClient = useQueryClient()

  const defaultHeaders = useMemo(() => {
    const headers: Record<string, string> = {}
    if (authHeaders) {
      Object.assign(headers, authHeaders)
    }
    if (accessSecret) {
      headers['X-ACCESS-SECRET'] = String(accessSecret)
    }
    return headers
  }, [accessSecret, authHeaders])

  const headerFingerprint = useMemo(() => {
    return Object.keys(defaultHeaders).length > 0 ? JSON.stringify(defaultHeaders) : ''
  }, [defaultHeaders])

  const fetchPage = useCallback(
    async (url: string): Promise<PaginatedResponse<any>> => {
      const headers =
        Object.keys(defaultHeaders).length > 0 ? defaultHeaders : undefined
      return queryClient.fetchQuery({
        queryKey: ['part-children', url, headerFingerprint],
        queryFn: () => fetchChildrenPage(url, headers),
        // Enable caching: dedupe identical children pages within short window
        staleTime: CHILDREN_STALE_TIME_MS,
        gcTime: CHILDREN_GC_TIME_MS,
        retry: 0,
      })
    },
    [defaultHeaders, headerFingerprint, queryClient]
  )

  const fetchAllChildren = useCallback(
    async (baseUrl: string, params: string[] = []) => {
      let nextUrl: string | null =
        params.length > 0 ? `${baseUrl}?${params.join('&')}` : baseUrl
      const children: APIArticle[] = []
      while (nextUrl) {
        const response = await fetchPage(nextUrl)
        const pageResults = Array.isArray(response.results)
          ? response.results.map((articleResponse: any) => APIArticleFromJSON(articleResponse))
          : []
        children.push(...pageResults)
        nextUrl = response.next ?? null
      }
      return children
    },
    [fetchPage]
  )

  const buildFetch = useCallback(
    async (part: Part) => {
      if (!part?.self) {
        throw new Error('Part self is required to load children')
      }

      const articleSelf = part.self
      const articlePk = getPKfromSelf(articleSelf)
      const requestChildrenBase = requestSelf
        ? `${requestSelf}parts/${articlePk}/children/`
        : `${articleSelf}children/`

      const visited = new Set<string>()
      const depthLimit = Number.isFinite(maxDepth) ? maxDepth : Infinity

      const isRequestScoped =
        requestChildrenBase.includes('/request/requests/') &&
        requestChildrenBase.includes('/parts/')
      const requestBasePrefix = isRequestScoped
        ? `${requestChildrenBase.split('/parts/')[0]}/parts/`
        : undefined

      const dfs = async (node: APIArticle, depth: number): Promise<void> => {
        const key = node.self
        if (!key || visited.has(key)) return
        visited.add(key)

        if (node.hasChildren === false) {
          onNode(node, [])
          return
        }

        let computedUrl: string
        if (requestBasePrefix) {
          const pk = getPKfromSelf(node.self)
          computedUrl = `${requestBasePrefix}${pk}/children/`
        } else {
          computedUrl = `${node.self}children/`
        }

        let children: APIArticle[] = []
        try {
          children = await fetchAllChildren(computedUrl, parameter)
        } catch (err: any) {
          const statusCode =
            typeof err === 'object' && err !== null && 'status' in err
              ? (err as { status?: number }).status
              : undefined
          if (statusCode === 404) {
            log.warn(
              'Children endpoint 404 for',
              node.name || node.number,
              '-> fallback to items/articles children'
            )
            children = await fetchAllChildren(`${node.self}children/`)
          } else {
            throw err
          }
        }

        onNode(node, children)

        if (depth >= depthLimit) return

        for (const child of children) {
          if (child.hasChildren) {
            await dfs(child, depth + 1)
          }
        }
      }

      try {
        await dfs(part as unknown as APIArticle, 0)
        onFinal()
      } catch (err) {
        if (onError) {
          onError(err)
        }
        throw err
      }
    },
    [fetchAllChildren, maxDepth, onError, onFinal, onNode, parameter, requestSelf]
  )

  const loadChildren = useCallback(
    async (part: Part) => {
      setStatus('pending')
      setError(null)
      try {
        await buildFetch(part)
        setStatus('success')
      } catch (err) {
        log.error('Failed to load part children', err)
        setStatus('error')
        setError(err as Error)
        throw err
      }
    },
    [buildFetch]
  )

  return {
    loadChildren,
    isLoading: status === 'pending',
    isError: status === 'error',
    error,
  }
}
